import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getCurrentUser: () => api.get('/auth/me'),
  sendOTP: (mobile) => api.post('/auth/send-otp', { mobile }),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  mobileLogin: (data) => api.post('/auth/mobile-login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const brandsAPI = {
  getAll: () => api.get('/brands'),
  getOne: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
};

export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  getCalendar: (brandId) => api.get('/content/calendar', { params: { brand_id: brandId } }),
  getMedia: (brandId) => api.get('/content/media', { params: { brand_id: brandId } }),
};

export const dashboardAPI = {
  getOverview: (params) => api.get('/dashboard/overview', { params }),
  getAnalytics: (brandId, period) => api.get('/dashboard/analytics', { params: { brand_id: brandId, period } }),
};

export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getCurrent: () => api.get('/subscription/my-subscription'),
  getAll: () => api.get('/subscription/all-subscriptions'),
  createTrial: () => api.post('/subscription/create-trial'),
  createOrder: (data) => api.post('/subscription/create-order', data),
  verifyPayment: (data) => api.post('/subscription/verify-payment', data),
  create: (data) => api.post('/subscription', data),
  cancel: (id) => api.post(`/subscription/${id}/cancel`),
};

export const onboardingAPI = {
  submit: (data) => api.post('/onboarding/submit', data),
  getStatus: () => api.get('/onboarding/status'),
  getDetails: () => api.get('/onboarding/details'),
  update: (data) => api.put('/onboarding/update', data),
};

export const invoiceAPI = {
  generate: (subId) => api.post(`/invoice/generate/${subId}`),
  download: (invoiceNum) => api.get(`/invoice/download/${invoiceNum}`, { responseType: 'blob' }),
  getMyInvoices: () => api.get('/invoice/my-invoices'),
};

export const automationAPI = {
  runNow: () => api.post('/automation/run-now'),
  getWeeklyReports: () => api.get('/automation/weekly-reports'),
  getMonthlyReports: () => api.get('/automation/monthly-reports'),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  toggleUserStatus: (userId) => api.post(`/admin/users/${userId}/toggle-status`),
  cancelSubscription: (userId, subId) => api.post(`/admin/users/${userId}/subscriptions/${subId}/cancel`),
  getAllInvoices: (params) => api.get('/admin/invoices', { params }),
  getWeeklyReports: (params) => api.get('/admin/reports/weekly', { params }),
};

export const infoAPI = {
  getBrandInfo: () => api.get('/info'),
};

export default api;
